import { Flex, Text } from '@chakra-ui/react';
import { faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  FormFeedback,
  Input,
  Modal,
  useToast
} from '@morressier/alembic';
import { SubmitHandler, useForm } from 'react-hook-form';
import { saveFilterByView } from 'src/api/mutations';
import { IMyReviewsFilters, IMySubmissionFilter } from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import ListView from 'src/layouts/ListView';
import { IEarlyAssessmentListFilters } from 'src/pages/MyTask/Assessments/hooks';
import { IAdjudicationsListFilters } from 'src/pages/MyTask/Adjudications/hooks';

interface Props {
  isOpen: boolean;
  onClose: (name: null | string) => void;
  filter:
    | IMySubmissionFilter
    | IMyReviewsFilters
    | IEarlyAssessmentListFilters
    | IAdjudicationsListFilters;
  view: string;
}
interface IFormData {
  name: string;
}

const SaveFiltersModal: React.FC<Props> = ({
  isOpen,
  onClose,
  filter,
  view
}) => {
  const savedFilterValidator = z.object({
    name: z.string().trim().min(1, 'Please enter filter name.')
  });
  const { register, handleSubmit, formState, setError } = useForm({
    defaultValues: {
      name: ''
    },
    resolver: zodResolver(savedFilterValidator)
  });
  const SaveSubmissionFilterMutation = saveFilterByView(view);
  const toast = useToast();
  const onSubmit: SubmitHandler<IFormData> = async data => {
    const { name } = data;
    const res = await SaveSubmissionFilterMutation.mutateAsync({
      filters: filter,
      name
    });
    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `${name} Filter Saved`,
        description: `Access "Saved Filters" to manage your filters`,
        isClosable: true
      });

      return onClose(name);
    }
    setError('name', {
      type: 'manual',
      message: 'This filter name is in use. Please choose another filter name.'
    });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose(null)}
      width="650px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <ListView>
        <ListView.Title>
          <Flex flexDirection="column" gap={4} alignItems="flex-start">
            <FontAwesomeIcon icon={faFloppyDisk} fontSize="42px" />
            <Text textStyle="headingGroup">Save Filter</Text>
          </Flex>
        </ListView.Title>

        <ListView.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column" gap={4}>
              <FieldLabel label="Filter Name" subText="" isRequired />
              <Input
                {...register('name')}
                isInvalid={!!formState.errors.name?.message}
                placeholder="Enter a filter name"
              />
              {formState.errors.name?.message ? (
                <FormFeedback variant="error">
                  {formState.errors.name.message}
                </FormFeedback>
              ) : null}

              <Flex gap={2} justifyContent="flex-end" marginTop={3}>
                <Button variant="secondary" onClick={() => onClose(null)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Save Filter
                </Button>
              </Flex>
            </Flex>
          </form>
        </ListView.Body>
      </ListView>
    </Modal>
  );
};

export default SaveFiltersModal;
